/**
 * API 客户端 + 本地身份存储。
 *
 * 两个约定：
 *   1. **一台设备可以属于多个冰箱**。本地存一份列表（冰箱 id / 名字 / 钥匙），
 *      其中一个是"当前"；每个请求带当前那把钥匙。切换冰箱 = 换个 id 重发请求，
 *      不需要重新登录，也不需要网络往返。
 *   2. 服务端的错误码（household_required / unauthorized / llm_not_configured …）
 *      会被翻译成带语义的异常，让调用方知道该怎么办 ——
 *      是"回引导页"、"填口令"，还是"改用手工输入"。
 *
 * 关于钥匙的存储位置：localStorage。这不是最好的地方（XSS 能读走），
 * 但这个应用没有第三方脚本、没有用户生成内容，XSS 面几乎为零；
 * 换成 httpOnly Cookie 反而要处理 CSRF，得不偿失。
 * 更重要的是：**钥匙可以随时被主人踢掉**，这才是真正的兜底。
 */

import type {
  BuyItem,
  DeviceEntry,
  DietProfile,
  FridgeItem,
  GenerateRecipeResponse,
  Household,
  HouseholdDetail,
  HouseholdSession,
  IntakeDraft,
  IntakeSource,
  InviteCode,
  MealBrief,
  Recipe,
} from '@fridge/shared';

import { HOUSEHOLD_KEY_HEADER } from '@fridge/shared';

const APP_TOKEN_KEY = 'fridge.app_token';
const SESSIONS_KEY = 'fridge.households';
const ACTIVE_KEY = 'fridge.active_household';

/* ------------------------------------------------------------------ */
/* 错误                                                                */
/* ------------------------------------------------------------------ */

/** 站点口令不对（只有部署方开了 APP_TOKEN 才会遇到） */
export class UnauthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnauthError';
  }
}

/** 没有钥匙，或者钥匙已经失效了 —— 该回引导页 */
export class HouseholdRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HouseholdRequiredError';
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    /** 服务端建议的降级动作 */
    public readonly fallback: 'manual' | 'none' = 'none',
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/* ------------------------------------------------------------------ */
/* 本地存储                                                            */
/* ------------------------------------------------------------------ */

export interface StoredHousehold {
  id: string;
  name: string;
  /** 设备凭证。用户永远看不到它，但它是这台设备进入这个冰箱的唯一凭据。 */
  key: string;
  /**
   * 有几台设备。**只有进过的冰箱才知道**（服务端只回报当前冰箱的详情），
   * 所以是可选的。切换器里显示它是为了让人一眼确认"这还是我和老婆那台"，
   * 而不是一个完全陌生的东西。
   */
  memberCount?: number;
}

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* 隐私模式下写不了，忽略即可 —— 代价是这次会话关掉就没了 */
  }
}

/** 本机记录过的全部冰箱。顺序 = 加入顺序。 */
export function listStoredHouseholds(): StoredHousehold[] {
  const raw = readJSON<unknown>(SESSIONS_KEY, []);
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is StoredHousehold =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as StoredHousehold).id === 'string' &&
      typeof (item as StoredHousehold).key === 'string',
  );
}

export function getActiveHouseholdId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

export function getActiveHousehold(): StoredHousehold | null {
  const all = listStoredHouseholds();
  const activeId = getActiveHouseholdId();
  return all.find((h) => h.id === activeId) ?? all[0] ?? null;
}

export function setActiveHousehold(id: string): void {
  try {
    localStorage.setItem(ACTIVE_KEY, id);
  } catch {
    /* 同上 */
  }
}

/**
 * 记住一个新冰箱（创建或加入成功之后调）并把它设为当前。
 * 同一个冰箱重复加入会覆盖钥匙 —— 这正是我们想要的：
 * 用新邀请码进来一次，旧钥匙立刻作废，不会留下两把。
 */
export function rememberHousehold(session: HouseholdSession): void {
  const all = listStoredHouseholds().filter((h) => h.id !== session.household.id);
  all.push({
    id: session.household.id,
    name: session.household.name,
    key: session.householdKey,
    memberCount: session.household.memberCount,
  });
  writeJSON(SESSIONS_KEY, all);
  setActiveHousehold(session.household.id);
}

export function renameStoredHousehold(id: string, name: string): void {
  const all = listStoredHouseholds().map((h) => (h.id === id ? { ...h, name } : h));
  writeJSON(SESSIONS_KEY, all);
}

/**
 * 把服务端刚告诉我们的信息写回本地缓存（名字被别人改过、多了台设备）。
 * 只改元信息，不碰钥匙。
 */
export function updateStoredHouseholdMeta(
  id: string,
  patch: { name?: string; memberCount?: number },
): void {
  const all = listStoredHouseholds().map((h) =>
    h.id === id
      ? {
          ...h,
          name: patch.name ?? h.name,
          memberCount: patch.memberCount ?? h.memberCount,
        }
      : h,
  );
  writeJSON(SESSIONS_KEY, all);
}

/** 忘掉一台冰箱（钥匙失效、或者用户主动退出） */
export function forgetHousehold(id: string): void {
  const all = listStoredHouseholds().filter((h) => h.id !== id);
  writeJSON(SESSIONS_KEY, all);
  if (getActiveHouseholdId() === id) {
    setActiveHousehold(all[0]?.id ?? '');
  }
}

/* ---- 站点口令（可选的外层门） ---- */

export function getToken(): string {
  try {
    return localStorage.getItem(APP_TOKEN_KEY) ?? '';
  } catch {
    return '';
  }
}

export function setToken(token: string): void {
  try {
    if (token) localStorage.setItem(APP_TOKEN_KEY, token);
    else localStorage.removeItem(APP_TOKEN_KEY);
  } catch {
    /* 同上 */
  }
}

/* ------------------------------------------------------------------ */
/* 请求                                                                */
/* ------------------------------------------------------------------ */

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** 引导流程专用：这些接口本来就还没有钥匙 */
  anonymous?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers['x-app-token'] = token;
  if (!options.anonymous) {
    const active = getActiveHousehold();
    if (active?.key) headers[HOUSEHOLD_KEY_HEADER] = active.key;
  }
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error;
    throw new ApiError('连不上服务器，检查一下网络或服务是否在跑。', 'network_error');
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    | Record<string, unknown>
    | null;

  if (response.ok) return payload as T;

  const message =
    typeof payload?.message === 'string' ? payload.message : `请求失败（HTTP ${response.status}）`;
  const code = typeof payload?.error === 'string' ? payload.error : 'unknown_error';
  const fallback = payload?.fallback === 'manual' ? 'manual' : 'none';

  if (response.status === 401) {
    // 两种 401 的处置完全不同：口令不对是"去填口令"，
    // 没有钥匙是"回引导页"。靠服务端给的 error 码区分。
    if (code === 'household_required') throw new HouseholdRequiredError(message);
    throw new UnauthError(message);
  }
  throw new ApiError(message, code, fallback);
}

/* ------------------------------------------------------------------ */
/* 引导与冰箱                                                          */
/* ------------------------------------------------------------------ */

export interface OnboardingInfo {
  /** 服务端开了口令门 —— 引导页要多出一个口令输入框 */
  authEnabled: boolean;
  nameMax: number;
}

export const fetchOnboarding = () =>
  request<OnboardingInfo>('/api/households/onboarding', { anonymous: true });

export const createHousehold = (name: string, deviceLabel?: string) =>
  request<HouseholdSession>('/api/households', {
    method: 'POST',
    body: { name, deviceLabel },
    anonymous: true,
  });

export const joinHousehold = (code: string, deviceLabel?: string) =>
  request<HouseholdSession>('/api/households/join', {
    method: 'POST',
    body: { code, deviceLabel },
    anonymous: true,
  });

export const fetchHouseholdDetail = () => request<HouseholdDetail>('/api/households/current');

export const renameHousehold = (name: string) =>
  request<HouseholdDetail>('/api/households/current', { method: 'PATCH', body: { name } });

export const issueInvite = () =>
  request<InviteCode>('/api/households/current/invite', { method: 'POST', body: {} });

export const revokeInvite = () =>
  request<{ removed: number }>('/api/households/current/invite', {
    method: 'DELETE',
    body: {},
  });

export const fetchDevices = () =>
  request<{ devices: DeviceEntry[] }>('/api/households/current/devices');

export const revokeDevice = (id: string) =>
  request<{ ok: true; wasSelf: boolean }>(`/api/households/current/devices/${id}`, {
    method: 'DELETE',
  });

export type { Household, HouseholdDetail, HouseholdSession, InviteCode, DeviceEntry };

/* ------------------------------------------------------------------ */
/* 健康与概览                                                          */
/* ------------------------------------------------------------------ */

export interface Overview {
  authEnabled: boolean;
  capabilities: { textLlm: boolean; visionLlm: boolean; transcribe: boolean };
  models: { text: string | null; vision: string | null };
  counts: { fridgeItems: number; recipes: number };
}

export const fetchOverview = () => request<Overview>('/api/overview');

/* ------------------------------------------------------------------ */
/* 冰箱                                                                */
/* ------------------------------------------------------------------ */

export interface FridgeResponse {
  items: FridgeItem[];
  total: number;
}

export const fetchFridge = () => request<FridgeResponse>('/api/fridge');

export const submitIntake = (source: IntakeSource, items: IntakeDraft['items']) =>
  request<{ items: FridgeItem[]; merged: string[] }>('/api/fridge/intake', {
    method: 'POST',
    body: { source, items },
  });

export const patchFridgeItem = (
  id: string,
  patch: Partial<Pick<FridgeItem, 'name' | 'qty' | 'unit' | 'category' | 'storedAt'>>,
) => request<FridgeItem>(`/api/fridge/${id}`, { method: 'PATCH', body: patch });

export const deleteFridgeItem = (id: string) =>
  request<{ ok: true }>(`/api/fridge/${id}`, { method: 'DELETE' });

export const consumeItems = (entries: Array<{ name: string; qty?: number }>) =>
  request<{ removed: string[]; decreased: string[] }>('/api/fridge/consume', {
    method: 'POST',
    body: { entries },
  });

/* ------------------------------------------------------------------ */
/* 录入适配器（服务端侧）                                              */
/* ------------------------------------------------------------------ */

export interface IntakeCapabilities {
  textParse: boolean;
  vision: boolean;
  transcribe: boolean;
  manualAlwaysAvailable: true;
}

export const fetchIntakeCapabilities = () =>
  request<IntakeCapabilities>('/api/intake/capabilities', { anonymous: true });

export const parseText = (text: string, source: 'voice' | 'manual' = 'voice', signal?: AbortSignal) =>
  request<IntakeDraft>('/api/intake/parse/text', {
    method: 'POST',
    body: { text, source },
    signal,
  });

export const parseImage = (image: string) =>
  request<IntakeDraft>('/api/intake/parse/image', { method: 'POST', body: { image } });

/* ------------------------------------------------------------------ */
/* 待买清单                                                            */
/* ------------------------------------------------------------------ */

export interface BuyListResponse {
  items: BuyItem[];
  openCount: number;
  checkedCount: number;
}

export const fetchBuyList = () => request<BuyListResponse>('/api/buylist');

export const addBuyItems = (items: Array<{ name: string; amount?: string | null }>) =>
  request<{ items: BuyItem[]; added: number }>('/api/buylist', {
    method: 'POST',
    body: { items },
  });

export const patchBuyItem = (
  id: string,
  patch: { name?: string; amount?: string | null; checked?: boolean },
) => request<BuyItem>(`/api/buylist/${id}`, { method: 'PATCH', body: patch });

export const deleteBuyItem = (id: string) =>
  request<{ ok: true }>(`/api/buylist/${id}`, { method: 'DELETE' });

export const clearCheckedBuyItems = () =>
  request<{ removed: number }>('/api/buylist/clear-checked', { method: 'POST', body: {} });

/** 买回来的东西一键入库 */
export const moveBuyItemsToFridge = (ids?: string[]) =>
  request<{ imported: number; merged: number; items: FridgeItem[] }>('/api/buylist/to-fridge', {
    method: 'POST',
    body: ids ? { ids } : {},
  });

/* ------------------------------------------------------------------ */
/* 偏好                                                                */
/* ------------------------------------------------------------------ */

export interface PrefsResponse {
  profile: DietProfile;
  lastBrief: MealBrief;
  defaultBrief: MealBrief;
}

export const fetchPrefs = () => request<PrefsResponse>('/api/prefs');

export const saveProfile = (patch: Partial<Omit<DietProfile, 'updatedAt'>>) =>
  request<DietProfile>('/api/prefs/profile', { method: 'PUT', body: patch });

export const saveBrief = (brief: MealBrief) =>
  request<MealBrief>('/api/prefs/brief', { method: 'PUT', body: brief });

/* ------------------------------------------------------------------ */
/* 菜谱                                                                */
/* ------------------------------------------------------------------ */

export const generateRecipes = (brief: MealBrief, count = 3) =>
  request<GenerateRecipeResponse & { buyItemsAdded: number; rejected: string[] }>(
    '/api/recipes/generate',
    { method: 'POST', body: { brief, count } },
  );

export const fetchRecipes = (limit = 30, favoriteOnly = false) =>
  request<{ items: Recipe[] }>(`/api/recipes?limit=${limit}&favorite=${favoriteOnly}`);

export const favoriteRecipe = (id: string, favorite: boolean) =>
  request<Recipe>(`/api/recipes/${id}/favorite`, { method: 'PATCH', body: { favorite } });

export const deleteRecipe = (id: string) =>
  request<{ ok: true }>(`/api/recipes/${id}`, { method: 'DELETE' });

/** 把某道菜缺的材料补进待买清单 */
export const pushMissingToBuyList = (recipeId: string) =>
  request<{ added: number; items: BuyItem[] }>(`/api/recipes/${recipeId}/missing-to-buylist`, {
    method: 'POST',
    body: {},
  });
